import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  deleteMarketMapKey,
  updateProperty,
  updateToggleLabelMapKey,
} from "@/features/auth/interactiveSurveySlice";
import { Property } from "@/features/survey/types";
import Analytics from "@/services/Analytics";
import { useUpdatePropertyShortlistMutation } from "@/services/base";
import {
  useAddSurveyMapKeysMutation,
  useDeleteSurveyMapKeyMutation,
  useUpdateSurveyMapKeyMutation,
} from "@/services/mapKeysService";
import { interactiveSurveyDetailsApi } from "@/services/survey";
import { surveyGroupApi } from "@/services/surveyGroupService";
import {
  getIconDataWithoutShortlist,
  getPropertyCountByPinColor,
  iconColorShortlist,
} from "@/shared/utils/map";
import { isShortlistEnabled } from "@/shared/utils/propertyUtils";
import { EmeraldButton } from "@emerald-react/button";
import { MouseEvent } from "react";

interface ShortlistToggleButtonProps {
  size?: "Medium" | "Small";
  property: Property;
}

/**
 * ShortlistToggleButton Component
 *
 * A reusable button component for toggling property shortlist status.
 * Handles API calls and loading states.
 */
const ShortlistToggleButton = ({
  size = "Small",
  property,
}: ShortlistToggleButtonProps) => {
  const [updatePropertyShortlist, { isLoading: isUpdatingShortlist }] =
    useUpdatePropertyShortlistMutation();
  const [updateSurveyMapKey] = useUpdateSurveyMapKeyMutation();
  const [addSurveyMapKeys] = useAddSurveyMapKeysMutation();
  const [deleteSurveyMapKey] = useDeleteSurveyMapKeyMutation();
  const dispatch = useAppDispatch();
  const { currentSurvey, currentSurveyGroup } = useAppSelector(
    (state) => state.interactiveSurvey,
  );
  const mapKeys = useAppSelector(
    (state) => state.interactiveSurvey.marketMapKeys,
  );

  // Don't render if shortlist is disabled for this survey/group
  if (!isShortlistEnabled(currentSurvey, currentSurveyGroup)) {
    return null;
  }

  // Handle shortlist toggle
  const handleShortlistToggle = async (event: MouseEvent) => {
    // Prevent the click from bubbling up to the property card click handler
    event.stopPropagation();

    if (isUpdatingShortlist) return;
    const mapDataColor =
      getIconDataWithoutShortlist(property)?.iconColor?.toLowerCase() || "";
    const isCurrentLocation = property?.isCurrentLocation ?? false;
    const matchingMapKey = mapKeys?.find(
      (key) =>
        key.color?.toLowerCase() === mapDataColor &&
        key.isCurrentLocation === isCurrentLocation &&
        key.category === "Property",
    );
    const propertyCountByPinColor = getPropertyCountByPinColor(
      currentSurvey?.properties || [],
    );
    const pinKey = `${mapDataColor}|${isCurrentLocation}|Property`;

    try {
      if (
        (propertyCountByPinColor[pinKey] === 1 || property.isClientShortlist) &&
        matchingMapKey
      ) {
        await updateSurveyMapKey({
          mapKeyId: matchingMapKey?.mapKeyId!,
          surveyId: currentSurvey?.id!,
          isShortlisted: !property.isClientShortlist,
        });
        dispatch(
          updateToggleLabelMapKey({
            ...matchingMapKey,
            isShortlisted: !property.isClientShortlist,
          }),
        );
      }

      const isShortlisting = !property.isClientShortlist;
      const shortlistMapKey = mapKeys?.find(
        (key) =>
          key.color?.toLowerCase() === iconColorShortlist &&
          key.category === "Property",
      );

      if (isShortlisting && !shortlistMapKey) {
        // Create the "Shortlisted Site" map key when first shortlisting a property
        await addSurveyMapKeys({
          surveyId: currentSurvey?.id!,
          mapKeys: [
            {
              label: "Shortlisted Site",
              color: iconColorShortlist,
              active: true,
              isCurrentLocation: false,
              order: 0,
              category: "Property",
            },
          ],
        });
      } else if (!isShortlisting && shortlistMapKey) {
        // When un-shortlisting, delete the "Shortlisted Site" map key if no other
        // properties remain shortlisted (excluding the one being un-shortlisted).
        const otherShortlisted = currentSurvey?.properties?.filter(
          (p) => p.isClientShortlist && p.id !== property.id && !p.isHidden,
        );
        if (!otherShortlisted?.length && shortlistMapKey.mapKeyId) {
          await deleteSurveyMapKey({
            mapKeyId: shortlistMapKey.mapKeyId,
            surveyId: currentSurvey?.id!,
          });
          dispatch(deleteMarketMapKey(shortlistMapKey.mapKeyId));
        }
      }

      const updatedProperty = await updatePropertyShortlist({
        propertyId: property.id,
        isClientShortlist: !property.isClientShortlist,
      }).unwrap();

      // Update the Redux store with the updated property returned from the API
      dispatch(updateProperty(updatedProperty));

      // Keep cached survey responses in sync, otherwise re-entering a survey or
      // the group views re-applies stale shortlist flags from the RTK cache.
      const replaceProperty = (properties?: Property[]) => {
        const index = properties?.findIndex((p) => p.id === updatedProperty.id);
        if (properties && index !== undefined && index !== -1) {
          properties[index] = updatedProperty;
        }
      };
      if (currentSurvey?.id) {
        dispatch(
          interactiveSurveyDetailsApi.util.updateQueryData(
            "getSurveyDetails",
            currentSurvey.id,
            (draft) => replaceProperty(draft.properties),
          ) as never,
        );
      }
      if (currentSurveyGroup?.surveyGroupId) {
        dispatch(
          surveyGroupApi.util.updateQueryData(
            "getSurveyGroupWithSurveys",
            currentSurveyGroup.surveyGroupId,
            (draft) =>
              replaceProperty(
                draft.surveys.find((s) => s.id === currentSurvey?.id)
                  ?.properties,
              ),
          ) as never,
        );
      }

      Analytics.send({
        event: "shortlistedProperty",
        survey_id: currentSurvey?.id,
        project_id: currentSurvey?.projectId,
        property_name: property.name,
        coordinates: `${property.latitude}, ${property.longitude}`,
        available_sf: property.availableSF,
      });
    } catch (error) {
      console.error("Error updating shortlist status:", error);
    }
  };

  return (
    <EmeraldButton
      icon={property.isClientShortlist ? "favorite" : "favorite_border"}
      label={property.isClientShortlist ? "Shortlisted" : "Shortlist"}
      type={property.isClientShortlist ? "Primary" : "Secondary"}
      onClick={handleShortlistToggle}
      disabled={isUpdatingShortlist}
      size={size}
      className="emerald-btn shortlist-toggle" // classname needed for GA
    />
  );
};

export default ShortlistToggleButton;
